/*
 * pic-nn-web-ui
 *
 * Web UI for neural network applications.
 *
 * @copyright 2020 VSTE CB <http://www.vstecb.cz/>.
 * @license See LICENSE file distributed with this source code for more information.
 */

import { inputNumber } from "../../shared/model/inputs/ModelInputNumber";
import { ModelHandler } from "../../shared/model/ModelHandler";
import { outputFormula } from "../../shared/model/outputs/ModelOutputFormula";
import { outputAPI } from "../../shared/model/outputs/ModelOutputAPI";
import { inputOptions } from "../../shared/model/inputs/ModelInputOptions";
import { inputText } from "../../shared/model/inputs/ModelInputText";
import { FPI_MSP_API_URL } from "../../config";

// tslint:disable: max-line-length

export function Model_FPI_MSP_2020_05_Handler() {

	const model = new ModelHandler({
		/* --- OBECNE --- */
		jmenoPodniku: inputText({
			label: "Jméno podniku"
		}),

		/* --- ROZVAHA --- */

		/* ROZVAHA / AKTIVA - Aktiva celkem */
		aktivaCelkem: inputNumber({
			label: "Aktiva celkem",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* ROZVAHA / AKTIVA - B. - Dlouhodobý majetek */
		dlouhodobyMajetek: inputNumber({
			label: "B. - Dlouhodobý majetek",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* ROZVAHA / AKTIVA - B. III. - Dlouhodobý finanční majetek */
		dlouhodobyFinMajetek: inputNumber({
			label: "B. III. - Dlouhodobý finanční majetek",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* ROZVAHA / AKTIVA - C. Oběžná aktiva */
		obeznaAktiva: inputNumber({
			label: "C. - Oběžná aktiva",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* ROZVAHA / AKTIVA - C. I. - Zásoby */
		zasoby: inputNumber({
			label: "C. I. - Zásoby",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* ROZVAHA / AKTIVA - C. II. 2. - Krátkodobé pohledávky */
		kratkodobePohledavky: inputNumber({
			label: "C. II. 2. - Krátkodobé pohledávky",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* ROZVAHA / AKTIVA - C. III. - Krátkodobý finanční majetek */
		kratkodobyFinMajetek: inputNumber({
			label: "C. III. - Krátkodobý finanční majetek",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* ROZVAHA / PASIVA - A. - Vlastní kapitál */
		vlastniKapital: inputNumber({
			label: "A. - Vlastní kapitál",
			suffix: "tis. Kč",
			required: true
		}),
		/* ROZVAHA / PASIVA - B. + C. - Cizí zdroje */
		ciziZdroje: inputNumber({
			label: "B. + C. - Cizí zdroje",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* ROZVAHA / PASIVA - C. II. - Krátkodobé závazky */
		kratkodobeZavazky: inputNumber({
			label: "C. II. - Krátkodobé závazky",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),

		/* --- VZZ --- */
		/* VZZ - I. - Tržby z prodeje výrobků a služeb */
		trzbyProdejVyrobkySluzby: inputNumber({
			label: "I. - Tržby z prodeje výrobků a služeb",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* VZZ - II. - Tržby za prodej zboží */
		trzbyProdejZbozi: inputNumber({
			label: "II. - Tržby za prodej zboží",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* VZZ - A. 1. - Náklady vynaložené na prodané zboží */
		nakladyNaProdaneZbozi: inputNumber({
			label: "A. 1. - Náklady vynaložené na prodané zboží",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* VZZ - A. 2. - Spotřeba materiálu */
		spotrebaMaterialu: inputNumber({
			label: "A. 2. - Spotřeba materiálu",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* VZZ - A. 3. Služby */
		sluzby: inputNumber({
			label: "A. 3. - Služby",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* VZZ - D. - Osobní náklady */
		osobniNaklady: inputNumber({
			label: "D. Osobní náklady",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* VZZ - E. 1. - Úpravy hodnot dlouhodobého nehmotného a hmotného majetku */
		odpisy: inputNumber({
			label: "E. 1. - Úpravy hodnot dlouhodobého nehmotného a hmotného majetku",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* VZZ - III. - Tržby z prodeje dlouhodobého majetku a materiálu */
		trzbyOstatniProvozni: inputNumber({
			label: "III. - Ostatní provozní výnosy",
			description: "Tržby z prodeje dlouhodobého majetku a materiálu",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* VZZ - IV. - Tržby z prodeje cenných papírů a podílů */
		trzbyFinMajetekPodily: inputNumber({
			label: "IV. - Tržby z prodeje cenných papírů a podílů",
			description: "Pokud není předmětem hlavní činnosti podniku obchod s cennými papíry, částka se v této položce neuvádí.",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* VZZ - J. - Nákladové úroky a podobné náklady */
		celkovyNakladovyUrok: inputNumber({
			label: "J. - Nákladové úroky a podobné náklady",
			suffix: "tis. Kč",
			required: true,
			min: 0
		}),
		/* VZZ - Výsledek hospodaření před zdaněním */
		ebt: inputNumber({
			label: "Výsledek hospodaření před zdaněním (EBT)",
			suffix: "tis. Kč",
			required: true
		}),
		/* VZZ - Výsledek hospodaření po zdanění */
		cistyZisk: inputNumber({
			label: "Výsledek hospodaření po zdanění (EAT)",
			suffix: "tis. Kč",
			required: true
		}),

		/* --- Doplnujici informace --- */

		/* Kraj: sídlo podnikání (uvádí se názvem kraje). */
		kraj: inputOptions({
			label: "Kraj",
			description: "Dle sídla (nikoliv místa podnikání).",
			required: true,
			options: {
				JHM: 1,
				JHC: 2,
				KVK: 2,
				VYS: 4,
				HKK: 5,
				LBK: 6,
				MSK: 7,
				OLK: 8,
				PAK: 9,
				PLK: 10,
				STC: 11,
				ZLK: 12,
				ULK: 13
			},
			optionLabels: [
				{ label: "Jihomoravský kraj", value: "JHM" },
				{ label: "Jihočeský kraj", value: "JHC" },
				{ label: "Karlovarský kraj", value: "KVK" },
				{ label: "Vysočina", value: "VYS" },
				{ label: "Královéhradecký kraj", value: "HKK" },
				{ label: "Liberecký kraj", value: "LBK" },
				{ label: "Moravskoslezský kraj", value: "MSK" },
				{ label: "Olomoucký kraj", value: "OLK" },
				{ label: "Pardubický kraj", value: "PAK" },
				{ label: "Plzeňský kraj", value: "PLK" },
				{ label: "Středočeský kraj", value: "STC" },
				{ label: "Zlínský kraj", value: "ZLK" },
				{ label: "Ústecký kraj", value: "ULK" }
			]
		}, "JHM"),
		/* NACE: zadává se písmeno sekce klasifikace ekonomických činnosti CZ NACE (blíže Český statistický úřad). */
		NACE: inputOptions({
			label: "CZ Nace",
			required: true,
			options: {
				CZ_A: 1,
				CZ_B: 2,
				CZ_C: 3,
				CZ_D: 4,
				CZ_E: 5,
				CZ_F: 6,
				CZ_G: 7,
				CZ_H: 8,
				CZ_I: 9,
				CZ_J: 10,
				CZ_K: 11,
				CZ_L: 12,
				CZ_M: 13,
				CZ_N: 14,
				CZ_P: 15,
				CZ_Q: 16,
				CZ_R: 17,
				CZ_S: 18
			},
			optionLabels: [
				{ label: "A. Zemědělství, lesnictví, rybářství", value: "CZ_A" },
				{ label: "B. Těžba a dobývání", value: "CZ_B" },
				{ label: "C. Zpracovatelský průmysl", value: "CZ_C" },
				{ label: "D. Výroba a rozvod elektřiny, plynu, tepla a klimatizovaného vzduchu", value: "CZ_D" },
				{ label: "E. Zásobování vodou; činnosti související s odpadními vodami, odpady a sanacemi", value: "CZ_E" },
				{ label: "F. Stavebnictví", value: "CZ_F" },
				{ label: "G. Velkoobchod a maloobchod; opravy a údržba motorových vozidel", value: "CZ_G" },
				{ label: "H. Doprava a skladování", value: "CZ_H" },
				{ label: "I. Ubytování, stravování a pohostinství", value: "CZ_I" },
				{ label: "J. Informační a komunikační činnosti", value: "CZ_J" },
				{ label: "K. Peněžnictví a pojišťovnictví", value: "CZ_K" },
				{ label: "L. Činnosti v oblasti nemovitostí", value: "CZ_L" },
				{ label: "M. Profesní, vědecké a technické činnosti", value: "CZ_M" },
				{ label: "N. Administrativní a podpůrné činnosti", value: "CZ_N" },
				{ label: "P. Vzdělávání", value: "CZ_P" },
				{ label: "Q. Zdravotní a sociální péče", value: "CZ_Q" },
				{ label: "R. Kulturní, zábavní a rekreační činnosti", value: "CZ_R" },
				{ label: "S. Ostatní činnosti", value: "CZ_S" }
			]
		}, "CZ_A")
	}, {
		/* ROE = [čistý zisk (EAT) / vlastní kapitál] */
		roe: outputFormula({
			fn: (inputs) => inputs.cistyZisk.get() / inputs.vlastniKapital.get(),
			label: "Rentabilita vlastního kapitálu (ROE)",
			decimals: 2,
			multiplier: 100,
			suffix: "%",
			description: `Jedná se o ukazatel ziskovosti. Podává informaci o míře zhodnocení vkladu vlastníků do podniku. Bohužel nebere v úvahu podstupované podnikatelské riziko. Minimální míra zhodnocení by se měla pohybovat nad úrovní bezrizikového výnosu (v podmínkách České republiky se jedná o úrokovou sazbu desetiletých státních dluhopisů). Ideálně by se měla výsledná hodnota pohybovat i výrazně nad úrokovou sazbou nabízenou bankami.`,
			resultLabels: [{
				condition: (state) => state.value > 1.0,
				prefix: "\\> 100%",
				content: `Jedná se o extrémně pozitivní výsledek. Zhodnocení vkladu vlastníků činí více než 100 % za zdaňovací období.`
			}, {
				condition: (state) => state.value > 0.5 && state.value <= 1.0,
				prefix: "\\> 50%",
				content: `Jedná se o vynikající výsledek. Hodnota ROE zohledňuje míru podnikatelského rizika. V tomto případě platí pravidlo, že čím větší riziko investor podstupuje, tím větší očekává výnos.`
			}, {
				condition: (state) => state.value > 0.2 && state.value <= 0.5,
				prefix: "\\> 20%",
				content: `Jedná se o nadprůměrný výsledek. Prostředky vlastníků jsou adekvátně zhodnoceny ve vztahu k podstupovanému podnikatelskému riziku.`
			}, {
				condition: (state) => state.value > 0.1 && state.value <= 0.2,
				prefix: "\\> 10%",
				content: `Jedná se o dobrý výsledek. Přesto neodráží však míru podstupovaného rizika. Nedochází tak k adekvátnímu využívání zdrojů podniku.`
			}, {
				condition: (state) => state.value >= 0.0 && state.value <= 0.1,
				prefix: "\\>= 0%",
				content: `Jedná se spíše o podprůměrný výsledek. V dolní části intervalu nepřekonává ani bezrizikovou míru zhodnocení vkladu. Vložené prostředky, pokud nebereme v úvahu případnou inflaci, neztrácejí na své hodnotě. Přesto je jejich zhodnocení vzhledem k podstupovanému riziku neadekvátní.`
			}, {
				condition: (state) => state.value < 0.0 && state.value >= -0.1,
				prefix: "< 0%",
				content: `Jedná se o špatný výsledek. Hodnota vkladů vlastníků se snižuje. Je však prostor pro budoucí zlepšení výsledků podniku.`
			}, {
				condition: (state) => state.value < -0.1 && state.value >= -0.3,
				prefix: "< -10%",
				content: `Jedná se o velmi špatný výsledek. Hodnota vkladů vlastníků se poměrně rychle snižuje. Podnik by měl rychle reagovat na vzniklou situaci.`
			}, {
				condition: (state) => state.value < -0.3 && state.value >= -1.0,
				prefix: "< -30%",
				content: `Jedná se o výrazně špatný výsledek. Hodnota vkladů vlastníků se snižuje velmi rychle. Vlastník by měl zvážit setrvání svého angažmá v podniku. V případě, že zůstane, by měl management přijmout velmi rychle opatření k nápravě takto negativní situace.`
			}, {
				condition: (state) => state.value < -1.0,
				prefix: "< -100%",
				content: `Jedná se o téměř zničující výsledek. Ano, zákon krátkodobě připouští záporný vlastní kapitál. Je to však situace mimořádná a podnik spěje k zániku.`
			}]
		}),
		/* ROA = [čistý zisk (EAT) / celková aktiva] */
		roa: outputFormula({
			fn: (inputs) => inputs.cistyZisk.get() / inputs.aktivaCelkem.get(),
			label: "Rentabilita aktiv (ROA)",
			decimals: 2,
			multiplier: 100,
			suffix: "%",
			description: `Jedná se o ukazatel ziskovosti. Podává informaci o míře zhodnocení majetku podniku. V podstatě nás informuje, jak efektivně podnik s majetkem zachází.`,
			resultLabels: [{
				condition: (state) => state.value > 1.0,
				prefix: "\\> 100%",
				content: `Jedná se o extrémně pozitivní výsledek. Majetek podniku byl za zdaňovací období zhodnocen na více než dvojnásobnou hodnotu.`
			}, {
				condition: (state) => state.value > 0.5 && state.value <= 1.0,
				prefix: "\\> 50%",
				content: `Jedná se o vynikající výsledek. Majetek podniku je využíván efektivně, přednostně pak k hodnototvorným činnostem.`
			}, {
				condition: (state) => state.value > 0.2 && state.value <= 0.5,
				prefix: "\\> 20%",
				content: `Jedná se o nadprůměrný výsledek. Prostředky podniku jsou využívány efektivně, především pak k hodnototvorným činnostem.`
			}, {
				condition: (state) => state.value > 0.1 && state.value <= 0.2,
				prefix: "\\> 10%",
				content: `Jedná se o dobrý výsledek. Lze však předpokládat nedostatky při nakládání s majetkem podniku. Část majetku pravděpodobně slouží k jiným než výrobně obchodním aktivitám.`
			}, {
				condition: (state) => state.value >= 0.0 && state.value <= 0.1,
				prefix: "\\>= 0%",
				content: `Jedná se spíše o podprůměrný výsledek. Pokud vezmeme v úvahu možnou inflaci, majetek podniku nepřináší téměř žádnou hodnotu.`
			}, {
				condition: (state) => state.value < 0.0 && state.value >= -0.1,
				prefix: "< 0%",
				content: `Jedná se o špatný výsledek. Hodnota majetku podniku se snižuje. Je však prostor pro budoucí zlepšení výsledků podniku. Je nutné, aby podnik zhodnotil, jaký majetek mu přináší hodnotu a jaký naopak generuje pouze náklady (na udržování).`
			}, {
				condition: (state) => state.value < -0.1 && state.value >= -0.3,
				prefix: "< -10%",
				content: `Jedná se o velmi špatný výsledek. Hodnota majetku podniku se poměrně rychle snižuje. Podnik by měl rychle reagovat na vzniklou situaci.`
			}, {
				condition: (state) => state.value < -0.3 && state.value >= -1.0,
				prefix: "< -30%",
				content: `Jedná se o výrazně špatný výsledek. Hodnota majetku podniku se snižuje velmi rychle. Podnik by měl zvážit výraznou restrukturalizaci, případně postupné ukončení své činnosti.`
			}, {
				condition: (state) => state.value < -1.0,
				prefix: "< -100%",
				content: `Jedná se o téměř zničující výsledek. Hodnota majetku klesla na nulu nebo dokonce pod ni (to je dáno rozdílem závazků a majetku podniku). Podnik již nemá z jakých prostředků uspokojovat závazky svých věřitelů. Měl by (i s ohledem na platné právní normy) ukončit svou činnost.`
			}]
		}),
		/* ROS = [čistý zisk (EAT) / (Tržby z prodeje vlastních výrobků a služeb + Tržby z prodeje zboží)] */
		ros: outputFormula({
			fn: (inputs) => inputs.cistyZisk.get() / ( inputs.trzbyProdejVyrobkySluzby.get() + inputs.trzbyProdejZbozi.get() ),
			label: "Rentabilita tržeb (ROS)",
			decimals: 2,
			multiplier: 100,
			suffix: "%",
			description: `Jedná se o ukazatel ziskovosti. Podává informaci o podílu čistého zisku na jedné koruně tržeb podniku. V podstatě nás informuje, jakou průměrnou marži podnik inkasuje.`,
			resultLabels: [{
				condition: (state) => state.value > 0.5,
				prefix: "\\> 50%",
				content: `Jedná se o vynikající výsledek. Podnik získává z jedné koruny tržeb více než 50 Haléřů čistého zisku. Jedná se tak velmi ziskovou aktivitu podniku.`
			}, {
				condition: (state) => state.value > 0.2 && state.value <= 0.5,
				prefix: "\\> 20%",
				content: `Jedná se o nadprůměrný výsledek. Každá koruna tržeb přinese podniku více než 20 Haléřů čistého zisku. Jde tak o velmi ziskovou aktivitu podniku.`
			}, {
				condition: (state) => state.value > 0.1 && state.value <= 0.2,
				prefix: "\\> 10%",
				content: `Jedná se o dobrý výsledek. Přesto už je cítit tlak na vyšší objem výkonů podniku. Tím podnik nahradí nižší míru marže v rámci svých tržeb.`
			}, {
				condition: (state) => state.value >= 0.0 && state.value <= 0.1,
				prefix: "\\>= 0%",
				content: `Jedná se spíše o podprůměrný výsledek. Pokud vezmeme v úvahu možnou inflaci, přináší tržby podniky minimální nebo dokonce nulovou hodnotu.`
			}, {
				condition: (state) => state.value < 0.0 && state.value >= -0.1,
				prefix: "< 0%",
				content: `Jedná se o špatný výsledek. Tržby se pohybují pod hranicí celkových nákladů podniku. To znamená, že se hodnota podniku při těchto výsledcích snižuje. Je však prostor učinit nápravná opatření.`
			}, {
				condition: (state) => state.value < -0.1 && state.value >= -0.3,
				prefix: "< -10%",
				content: `Jedná se o velmi špatný výsledek. Podnik už musí zvažovat, zda již nedosahuje bodu uzavření firmy (tj. rovnosti realizovaných tržeb a přímých nákladů). Pokud je rozdíl tržeb a přímých nákladů záporný, měl by vlastník firmu uzavřít.`
			}, {
				condition: (state) => state.value < -0.3,
				prefix: "< -30%",
				content: `Jedná se o výrazně špatný výsledek. Firma s takovýmto výsledkem nemůže dlouhodobě přežít.`
			}]
		}),

		/*
		 * Obrat zásob = [Tržby / zásoby (C.) - ROZVAHA]
		 *   Tržby = [Tržby za prodej zboží (I.) + Výkony (II.) + Tržby z prodeje dlouhodobého majetku a materiálu (III.) + Tržby z prodeje cenných papírů a podílů (VI.) - VZZ]
		 */
		obratZasob: outputFormula({
			fn: (inputs) => (
				  inputs.trzbyProdejVyrobkySluzby.get()
				+ inputs.trzbyProdejZbozi.get()
				+ inputs.trzbyOstatniProvozni.get()
				+ inputs.trzbyFinMajetekPodily.get()
			) / inputs.zasoby.get(),
			label: "Obrat zásob",
			decimals: 2,
			description: `Ukazatel aktivity podává informaci, kolikrát se zásoby přemění na tržby v průběhu roku.`,
			resultLabels: [{
				condition: (state) => state.value > 6,
				prefix: "\\> 6",
				content: `Hodnota ukazatele je vysoká a znamená, že podnik nakládá se zásobami velmi efektivně.`
			}, {
				condition: (state) => state.value >= 4.5 && state.value <= 6,
				prefix: "\\>= 4.5",
				content: `Jedná se o optimální hodnotu ukazatele. Znamená, že se zásoby přemění na tržby 4,5 krát a více do roka.`
			}, {
				condition: (state) => state.value < 4.5,
				prefix: "< 4.5",
				content: `Hodnota ukazatele je nižší, než je doporučená hodnota. Podnik by měl zefektivnit své hospodaření a nevytvářet velké množství zásob.`
			}]
		}),
		/*
		 * Obrat dlouhodobého majetku = [Tržby / Dlouhodobý majetek (B.) - ROZVAHA]
		 *   Tržby = [Tržby za prodej zboží (I.) + Výkony (II.) + Tržby z prodeje dlouhodobého majetku a materiálu (III.) + Tržby z prodeje cenných papírů a podílů (VI.) - VZZ]
		 */
		obratDlouhodobehoMajetku: outputFormula({
			fn: (inputs) => (
				  inputs.trzbyProdejVyrobkySluzby.get()
				+ inputs.trzbyProdejZbozi.get()
				+ inputs.trzbyOstatniProvozni.get()
				+ inputs.trzbyFinMajetekPodily.get()
			) / inputs.dlouhodobyMajetek.get(),
			label: "Obrat dlouhodobého majetku",
			decimals: 2,
			description: `Ukazatel aktivity podává informaci, kolikrát se dlouhodobý majetek přemění na tržby v průběhu roku.`,
			resultLabels: [{
				condition: (state) => state.value > 6,
				prefix: "\\> 6",
				content: `Hodnota ukazatele je vysoká a znamená, že podnik nakládá se dlouhodobým majetkem velmi efektivně.`
			}, {
				condition: (state) => state.value >= 5 && state.value <= 6,
				prefix: "\\>= 5",
				content: `Jedná se o optimální hodnotu ukazatele. Znamená, že se dlouhodobý majetek přemění na tržby 5 krát a více do roka.`
			}, {
				condition: (state) => state.value < 5,
				prefix: "< 5",
				content: `Hodnota ukazatele je nižší, než je doporučená hodnota. Podnik by měl zefektivnit používání dlouhodobé majetku, resp. by měl používat pouze provozně nutný dlouhodobý majetek.`
			}]
		}),
		/*
		 * Obrat majetku = [Tržby / Majetek]
		 *   Tržby = [Tržby za prodej zboží (I.) + Výkony (II.) + Tržby z prodeje dlouhodobého majetku a materiálu (III.) + Tržby z prodeje cenných papírů a podílů (VI.) - VZZ]
		 *   Majetek = [Dlouhodobý majetek (B.) + oběžná aktiva (C.) - ROZVAHA]
		 */
		obratMajetku: outputFormula({
			fn: (inputs) => (
				  inputs.trzbyProdejVyrobkySluzby.get()
				+ inputs.trzbyProdejZbozi.get()
				+ inputs.trzbyOstatniProvozni.get()
				+ inputs.trzbyFinMajetekPodily.get()
			) / (
				inputs.dlouhodobyMajetek.get() + inputs.obeznaAktiva.get()
			),
			label: "Obrat majetku",
			decimals: 2,
			description: `Ukazatel aktivity podává informaci, kolikrát se majetek podniku přemění na tržby v průběhu roku.`,
			resultLabels: [{
				condition: (state) => state.value > 1.5,
				prefix: "\\> 1.5",
				content: `Hodnota ukazatele je vysoká a znamená, že podnik hospodaří s majetkem velmi efektivně.`
			}, {
				condition: (state) => state.value >= 1 && state.value <= 1.5,
				prefix: "\\>= 1",
				content: `Jedná se o optimální hodnotu ukazatele. Znamená, že se majetek podniku přemění na tržby jednou a vícekrát do roka.`
			}, {
				condition: (state) => state.value < 1,
				prefix: "< 1",
				content: `Hodnota ukazatele je nižší, než je doporučená hodnota. Podnik by měl zefektivnit své hospodaří s majetkem a využívat pouze majetek, který je provozně nutný.`
			}]
		}),

		/*
		 * Doba obrátky zásob = [360 * (zásoby / celková aktiva)]
		 *   Zásoby = Zásoby (C.I.) - ROZVAHA
		 *   Celková aktiva = Aktiva celkem - ROZVAHA
		 */
		dobaObratkyZasob: outputFormula({
			fn: (inputs) => 360 * ( inputs.zasoby.get() / inputs.aktivaCelkem.get() ),
			label: "Doba obrátky zásob",
			decimals: 2,
			description: `Ukazatel aktivity podává informaci, za kolik dnů se změní stávající zásoby na tržby.`,
			resultLabels: [{
				condition: (state) => state.value > 85,
				prefix: "\\> 85",
				content: `Hodnota ukazatele je vyšší, než je doporučená hodnota. To značí o neefektivním využívání zásob.`
			}, {
				condition: (state) => state.value >= 75 && state.value <= 85,
				prefix: "\\>= 75",
				content: `Hodnota ukazatele je na optimální úrovni. Zásoby plní svou funkci a zároveň nedochází k neefektivnímu využívání zdrojů.`
			}, {
				condition: (state) => state.value < 75,
				prefix: "< 75",
				content: `Hodnota ukazatel je na nižší než doporučené úrovni. Podnik tak zachází se zásobami velmi efektivně.`
			}]
		}),
		/*
		 * Doba obrátky dlouhodobého majetku = [360 * (dlouhodobý majetek / tržby)]
		 *   Dlouhodobý majetek = Dlouhodobý majetek (B.) - ROZVAHA
		 *   Tržby = [Tržby za prodej zboží (I.) + Výkony (II.) + Tržby z prodeje dlouhodobého majetku a materiálu (III.) + Tržby z prodeje cenných papírů a podílů (VI.) - VZZ]
		 */
		dobaObratkyDlouhodobehoMajetku: outputFormula({
			fn: (inputs) => 360 * ( inputs.dlouhodobyMajetek.get() / (
				  inputs.trzbyProdejVyrobkySluzby.get()
				+ inputs.trzbyProdejZbozi.get()
				+ inputs.trzbyOstatniProvozni.get()
				+ inputs.trzbyFinMajetekPodily.get()
			) ),
			label: "Doba obrátky dlouhodobého majetku",
			decimals: 2,
			description: `Ukazatel aktivity podává informaci, za kolik dnů se změní stávající dlouhodobý majetek na tržby.`,
			resultLabels: [{
				condition: (state) => state.value > 75,
				prefix: "\\> 75",
				content: `Hodnota ukazatele je vyšší, než je doporučená hodnota. To značí o neefektivním využívání dlouhodobého majetku.`
			}, {
				condition: (state) => state.value >= 67 && state.value <= 75,
				prefix: "\\>= 67",
				content: `Hodnota ukazatele je na optimální úrovni. Dlouhodobý majetek plní svou funkci a zároveň nedochází k neefektivnímu využívání zdrojů.`
			}, {
				condition: (state) => state.value < 67,
				prefix: "< 67",
				content: `Hodnota ukazatele je na, nižší než doporučené úrovni. Podnik tak zachází s dlouhodobým majetkem efektivně.`
			}]
		}),
		/*
		 * Doba obrátky majetku = [360 * (Majetek / Tržby)]
		 *   Majetek = [Dlouhodobý majetek (B.) + oběžná aktiva (C.) - ROZVAHA]
		 *   Tržby = [Tržby za prodej zboží (I.) + Výkony (II.) + Tržby z prodeje dlouhodobého majetku a materiálu (III.) + Tržby z prodeje cenných papírů a podílů (VI.) - VZZ]
		 */
		dobaObratkyMajetku: outputFormula({
			fn: (inputs) => 360 * (
				(
					  inputs.dlouhodobyMajetek.get()
					+ inputs.obeznaAktiva.get()
				) / (
					  inputs.trzbyProdejVyrobkySluzby.get()
					+ inputs.trzbyProdejZbozi.get()
					+ inputs.trzbyOstatniProvozni.get()
					+ inputs.trzbyFinMajetekPodily.get()
				)
			),
			label: "Doba obrátky majetku",
			decimals: 2,
			description: `Ukazatel aktivity podává informaci, za kolik dnů se změní stávající majetek na tržby.`,
			resultLabels: [{
				condition: (state) => state.value > 370,
				prefix: "\\> 370",
				content: `Hodnota ukazatele je vyšší, než je doporučená hodnota. To značí o neefektivním využívání majetku podniku.`
			}, {
				condition: (state) => state.value >= 350 && state.value <= 370,
				prefix: "\\>= 350",
				content: `Hodnota ukazatele je na optimální úrovni. Majetek plní svou funkci a zároveň nedochází k neefektivnímu využívání zdrojů.`
			}, {
				condition: (state) => state.value < 350,
				prefix: "< 350",
				content: `Hodnota ukazatele je na nižší, než doporučené, úrovni. Podnik tak zachází s majetkem efektivně.`
			}]
		}),

		/*
		 * Cizí kapitál/celkový kapitál = [Zadluženost]
		 *   Cizí kapitál = [cizí zdroje (B. + C.) - ROZVAHA]
		 *   Celkový kapitál = [Vlastní kapitál (A.) + cizí zdroje (B. + C.) - ROZVAHA]
		 */
		ciziKapitalCelkovyKapital: outputFormula({
			fn: (inputs) => inputs.ciziZdroje.get() / (
				  inputs.vlastniKapital.get()
				+ inputs.ciziZdroje.get()
			),
			label: "Zadluženost",
			decimals: 2,
			multiplier: 100,
			suffix: "%",
			description: `Poměr cizího kapitálu vůči celkovému kapitálu.`,
			resultLabels: [{
				condition: (state) => state.value > 1.0,
				prefix: "\\> 100%",
				content: `Zadluženost je na úrovni, která překračuje majetek podniku. Zákon tento stav umožňuje pouze na omezenou dobu. Poté je management podniku povinen ukončit činnost podniku.`
			}, {
				condition: (state) => state.value > 0.7 && state.value <= 1.0,
				prefix: "\\> 70%",
				content: `Zadluženost je velmi vysoká. To může mít pozitivní vliv na působení finanční páky. Podnik tím však zvyšuje podnikatelské riziko.`
			}, {
				condition: (state) => state.value > 0.55 && state.value <= 0.7,
				prefix: "\\> 55%",
				content: `Zadluženost je vyšší než doporučená hodnota. Neohrožuje však existenci podniku.`
			}, {
				condition: (state) => state.value >= 0.45 && state.value <= 0.55,
				prefix: "\\>= 45%",
				content: `Zadluženost je na optimální úrovni.`
			}, {
				condition: (state) => state.value < 0.45,
				prefix: "< 45%",
				content: `Zadluženost je nižší, než je doporučená hodnota. Podnik tak nevyužívá dostatečně výhod plynoucích z použití cizího kapitálu. Na druhou stranu tím snižuje riziko svého podnikání.`
			}]
		}),
		/*
		 * Ukazatel úrokového krytí I. = [EBIT / Celkový nákladový úrok]
		 *   EBIT = [Výsledek hospodaření před zdaněním + Nákladové úroky a podobné náklady (J.) - VZZ]
		 *   Celkový nákladový úrok = Nákladové úroky a podobné náklady (J.) - VZZ
		 */
		urokoveKryti1: outputFormula({
			fn: (inputs) => (
				inputs.ebt.get() + inputs.celkovyNakladovyUrok.get()
			) / inputs.celkovyNakladovyUrok.get(),
			label: "Ukazatel úrokového krytí I.",
			decimals: 2,
			description: `Ukazatel hodnotí schopnost podniku generovat zisk v takové výši, která pokryje závazek vůči věřiteli plynoucí z úroků za poskytnutý cizí kapitál. Ukazatel vychází z předpokladu, že podnik kryje svůj závazek vůči věřiteli například dlouhodobým majetkem a že tento majetek pokrývá celou výši závazku. Věřitel tak očekává od podniku především úhradu jeho zisku, úroku.`,
			resultLabels: [{
				condition: (state) => state.value > 3,
				prefix: "\\> 3",
				content: `Hodnota úrokového krytí je na dobré úrovni. Generovaný zisk umožňuje podniku bezproblémové platby za úroky věřiteli.`
			}, {
				condition: (state) => state.value <= 3,
				prefix: "<= 3",
				content: `Hodnota úrokového krytí je nízká. Podnik se může dostat do potíží s platbami úroků za vypůjčený cizí kapitál.`
			}]
		}),
		/*
		 * Ukazatel úrokového krytí II. = [(EBT + odpisy) / Úroky nákladové]
		 *   EBT = EBT - ROZVAHA
		 *   Odpisy = odpisy - ROZVAHA
		 *   Úroky nákladové = Nákladové úroky a podobné náklady (J.) - VZZ
		 */
		urokoveKryti2: outputFormula({
			fn: (inputs) => (
				inputs.ebt.get() + inputs.odpisy.get()
			) / inputs.celkovyNakladovyUrok.get(),
			label: "Ukazatel úrokového krytí II.",
			decimals: 2,
			description: `Ukazatel hodnotí schopnost podniku generovat zisk v takové výši, která pokryje závazek vůči věřiteli plynoucí z úroků za poskytnutý cizí kapitál. Ukazatel vychází z předpokladu, že podnik kryje svůj závazek vůči věřiteli například dlouhodobým majetkem a že tento majetek pokrývá celou výši závazku. Věřitel tak očekává od podniku především úhradu jeho zisku, úroku. Do výpočtu ukazatele byly zapojeny i odpisy. Znamená to, že i tyto prostředky jsou určeny k úhradě závazku vůči věřiteli. To znamená, že věřitel bude uspokojen. Podnik však nebude reprodukovat svůj majetek a za nějaký čas ztratí schopnost generovat prostředky na úhradu úroků.`,
			resultLabels: [{
				condition: (state) => state.value > 7,
				prefix: "\\> 7",
				content: `Hodnota úrokového krytí je na dobré úrovni. Generovaný zisk umožňuje podniku bezproblémové platby za úroky věřiteli. Podnik je zároveň schopen reprodukovat svůj dlouhodobý majetek a dlouhodobě prosperovat.`
			}, {
				condition: (state) => state.value >= 3 && state.value <= 7,
				prefix: "\\>= 3",
				content: `Hodnota úrokového krytí je nízká. Podnik je schopen hradit úroky svému věřiteli. Není však schopen reprodukovat svůj majetek. Dlouhodobě je takový stav neudržitelný.`
			}, {
				condition: (state) => state.value < 3,
				prefix: "< 3",
				content: `Hodnota úrokového krytí je nízká. Podnik se může dostat do potíží s platbami úroků za vypůjčený cizí kapitál.`
			}]
		}),

		/*
		 * Likvidita 1 = [Finanční majetek / Krátkodobé závazky]
		 *   Finanční majetek = [Dlouhodobý finanční majetek (B.III.) + krátkodobý finanční majetek (C.III.) - ROZVAHA]
		 *   Krátkodobé závazky = Krátkodobé závazky (C.II.) - ROZVAHA
		 */
		likvidita1: outputFormula({
			fn: (inputs) => (
				  inputs.dlouhodobyFinMajetek.get()
				+ inputs.kratkodobyFinMajetek.get()
			) / inputs.kratkodobeZavazky.get(),
			label: "Likvidita I.",
			decimals: 2,
			description: `Ukazatel tzv. peněžní likvidity informuje o tom, jak je schopen podnik pokrýt své krátkodobé závazky při použití svého finančního majetku.`,
			resultLabels: [{
				condition: (state) => state.value > 0.5,
				prefix: "\\> 0.5",
				content: `Peněžní likvidita je na velmi vysoké úrovni. Podnik je schopen ihned uhradit více než polovinu svých krátkodobých závazků. Výše likvidity však ukazuje, že management má spíše averzi k riziku. Pravděpodobně tak nenastane riziko platební neschopnosti. Na druhou stranu představuje taková situace neefektivní nakládání s aktivy podniku.`
			}, {
				condition: (state) => state.value > 0.25,
				prefix: "\\> 0.25",
				content: `Peněžní likvidita dosahuje obecně vysoké úrovně. Může se jednat o neefektivní nakládání s prostředky podniku. Může se však zároveň jednat o adekvátní krytí závazků podniku. To se však odvíjí od povahy a předmětu podnikání podniku. Obecně však platí, že nižší míra likvidity přináší vyšší efekt, avšak zároveň i vyšší míru podnikatelského rizika plynoucího z rizika nesplacení závazků firmy.`
			}, {
				condition: (state) => state.value >= 0.15 && state.value <= 0.5,
				prefix: "\\>= 0.15",
				content: `Peněžní likvidita dosahuje doporučené hodnoty, která je odvozena dlouhodobým pozorováním a odvozena jako hranice, při níž podnik podstupuje únosnou míru rizika a zároveň nedochází k výraznému plýtvání zdroji podniku.`
			}, {
				condition: (state) => state.value < 0.15,
				prefix: "< 0.15",
				content: `Jedná se o nižší, než doporučenou hodnotu peněžní likvidity. Podnik tím zvyšuje své podnikatelské riziko. To je na druhé straně vyvažováno vyšší mírou zhodnocení zdrojů podniku. Přesto by měl management zvážit, zda podnik dokáže včas a v plné výši hradit své závazky.`
			}]
		}),
		/*
		 * Likvidita 2 = [(finanční majetek + krátkodobé pohledávky) / krátkodobé závazky]
		 *   (finanční majetek + krátkodobé pohledávky) = [Dlouhodobý finanční majetek (B.III.) + krátkodobý finanční majetek (C.III.)) + krátkodobé pohledávky (C.II.2.) - ROZVAHA]
		 *   krátkodobé závazky = Krátkodobé závazky (C.II.) - ROZVAHA
		 */
		likvidita2: outputFormula({
			fn: (inputs) => (
				  inputs.dlouhodobyFinMajetek.get()
				+ inputs.kratkodobyFinMajetek.get()
				+ inputs.kratkodobePohledavky.get()
			) / inputs.kratkodobeZavazky.get(),
			label: "Likvidita II.",
			decimals: 2,
			description: `Ukazatel tzv. pohotové likvidity informuje o tom, jak je schopen podnik pokrýt své krátkodobé závazky při použití svého finančního majetku krátkodobých pohledávek.`,
			resultLabels: [{
				condition: (state) => state.value > 1.6,
				prefix: "\\> 1.6",
				content: `Pohotová likvidita je na velmi vysoké úrovni. Podnik je schopen prostřednictvím svých peněžních prostředků a pohledávek uhradit ihned více než 150 % svých krátkodobých závazků. Výše likvidity však ukazuje, že management má spíše averzi k riziku. Pravděpodobně tak nenastane riziko platební neschopnosti. Na druhou stranu představuje taková situace neefektivní nakládání s aktivy podniku.`
			}, {
				condition: (state) => state.value > 1.25 && state.value <= 1.6,
				prefix: "\\> 1.25",
				content: `Pohotová likvidita dosahuje obecně vysoké úrovně. Může se jednat o neefektivní nakládání s prostředky podniku. Může se však zároveň jednat o adekvátní krytí závazků podniku. To se však odvíjí od povahy a předmětu podnikání podniku. Obecně však platí, že nižší míra likvidity přináší vyšší efekt, avšak zároveň i vyšší míru podnikatelského rizika plynoucího z rizika nesplacení závazků firmy.`
			}, {
				condition: (state) => state.value >= 1.15 && state.value <= 1.25,
				prefix: "\\>= 1.15",
				content: `Pohotová likvidita dosahuje doporučené hodnoty, která je odvozena dlouhodobým pozorováním a odvozena jako hranice, při níž podnik podstupuje únosnou míru rizika a zároveň nedochází k výraznému plýtvání zdroji podniku.`
			}, {
				condition: (state) => state.value < 1.15,
				prefix: "< 1.15",
				content: `Jedná se o nižší, než doporučenou hodnotu pohotové likvidity. Podnik tím zvyšuje své podnikatelské riziko. To je na druhé straně vyvažováno vyšší mírou zhodnocení zdrojů podniku. Přesto by měl management zvážit, zda podnik dokáže včas a v plné výši hradit své závazky.`
			}]
		}),
		/*
		 * Likvidita 3 = [oběžná aktiva / krátkodobé závazky]
		 *   oběžná aktiva = Oběžná aktiva (C.) - ROZVAHA
		 *   krátkodobé závazky = Krátkodobé závazky (C.II.) - ROZVAHA
		 */
		likvidita3: outputFormula({
			fn: (inputs) => inputs.obeznaAktiva.get() / inputs.kratkodobeZavazky.get(),
			label: "Likvidita III.",
			decimals: 2,
			description: `Ukazatel tzv. běžné likvidity informuje o tom, jak je schopen podnik pokrýt své krátkodobé závazky při použití svého finančního majetku, pohledávek a zásob.`,
			resultLabels: [{
				condition: (state) => state.value > 2.5,
				prefix: "\\> 2.5",
				content: `Běžná likvidita je na velmi vysoké úrovni. Podnik je schopen prostřednictvím svých peněžních prostředků, pohledávek a zásob uhradit ihned více než 250 % svých krátkodobých závazků. Výše likvidity však ukazuje , že management má spíše averzi k riziku. Pravděpodobně tak nenastane riziko platební neschopnosti. Na druhou stranu představuje taková situace neefektivní nakládání s aktivy podniku.`
			}, {
				condition: (state) => state.value > 1.65 && state.value <= 2.5,
				prefix: "\\> 1.65",
				content: `Běžná likvidita dosahuje obecně vysoké úrovně. Může se jednat o neefektivní nakládání s prostředky podniku. Může se však zároveň jednat o adekvátní krytí závazků podniku. To se však odvíjí od povahy a předmětu podnikání podniku. Obecně však platí, že nižší míra likvidity přináší vyšší efekt, avšak zároveň i vyšší míru podnikatelského rizika plynoucího z rizika nesplacení závazků firmy.`
			}, {
				condition: (state) => state.value >= 1.55 && state.value <= 1.65,
				prefix: "\\>= 1.55",
				content: `Běžná likvidita dosahuje doporučené hodnoty, která je odvozena dlouhodobým pozorováním a odvozena jako hranice, při níž podnik podstupuje únosnou míru rizika a zároveň nedochází k výraznému plýtvání zdroji podniku.`
			}, {
				condition: (state) => state.value < 1.55,
				prefix: "< 1.55",
				content: `Jedná se o nižší, než doporučenou hodnotu běžné likvidity. Podnik tím zvyšuje své podnikatelské riziko. To je na druhé straně vyvažováno vyšší mírou zhodnocení zdrojů podniku. Přesto by měl management zvážit, zda podnik dokáže včas a v plné výši hradit své závazky.`
			}]
		}),

		/**
		 * EVA Equity - neuronova sit
		 */
		evaEquity: outputAPI<number>({
			// url: "https://nn.vstecb.on.priya.cz/fpi-msp-eva-v1",
			url: `${FPI_MSP_API_URL}/fpi-msp-eva-v1`,
			bodyFn: (inputs) => ({
				material_usage: inputs.spotrebaMaterialu.get(),
				cost_of_goods_sold: inputs.nakladyNaProdaneZbozi.get(),
				services: inputs.sluzby.get(),
				personal_expenses: inputs.osobniNaklady.get(),
				depreciation_of_fixed_assets: inputs.odpisy.get(),
				interest_expense: inputs.celkovyNakladovyUrok.get(),
				cz_region: inputs.kraj.get(),
				cz_nace: inputs.NACE.get()
			}),
			outputFn: (res) => res["predicted_output"],
			label: "EVA Equity",
			delay: 500,
			decimals: 2,
			description: `Ekonomická přidaná hodnota (Economic Value Added).`,
			// tslint:disable: no-trailing-whitespace
			resultLabels: [{
				condition: (state, handler) => handler.inputs.vlastniKapital.get() <= 0 && handler.inputs.cistyZisk.get() < 0 && state.value < 0,
				large: true,
				prefix: `
E <= 0  
Hospodářský výsledek < 0  
EVA Equity < 0
				`,
				content: `
Jedná se o velmi špatný výsledek, který předznamenává zánik podniku. Podnik nevyužívá efektu generátorů hodnoty. Majitel (manažer) má v tuto chvíli tyto možnosti:

1. Připraví podnik na vstup do likvidace a v rámci procesu likvidace činnost podniku ukončí.
2. V případě, že stav je mimořádný a lze očekávat jeho zlepší, je možné pokračovat v činnosti. Záporný vlastní kapitál však nemůže trvat dlouhodobě.
3. V případě, že majitel očekává zlepšení, byť stávající stav je již dlouhodobě trvající, je nutné, aby byl navýšen vlastní kapitál podniku tak, aby byl v součtu alespoň 0.
4. Provést výraznou restrukturalizaci za účelem zefektivnění chodu. Součástí musí být navýšení vlastního kapitálu podniku tak, aby byl v součtu alespoň 0.
				`
			}, {
				condition: (state, handler) => handler.inputs.vlastniKapital.get() > 0 && handler.inputs.cistyZisk.get() <= 0 && state.value <= 0,
				large: true,
				prefix: `
E > 0  
Hospodářský výsledek <= 0  
EVA Equity <= 0
				`,
				content: `Jedná se o velmi špatný výsledek, který předznamenává zánik podniku. Podnik nevyužívá efektu generátorů hodnoty. Přítomnost vlastního kapitálu však dává podniku prostor pro zlepšení využívání generátorů hodnotu a dosažení jejich případného potenciálu. Pokud však stav bude setrvávat dlouhodobě, měl by vlastník zvážit opuštění investice.`
			}, {
				condition: (state, handler) => handler.inputs.vlastniKapital.get() > 0 && handler.inputs.cistyZisk.get() > 0 && state.value < 0,
				large: true,
				prefix: `
E > 0  
Hospodářský výsledek > 0  
EVA Equity < 0
				`,
				content: `Výsledek není dobrý. Podnik nevyužívá efektu generátorů hodnoty dostatečně. Vlastník podstupuje zbytečně vyšší míru rizika, která neodpovídá dosaženého efektu (žádoucího zhodnocení jeho investice). Pokud stav bude dlouhodobě přetrvávat, měl by vlastník zvážit odchod z podniku a volit takovou investici, která se bude vyznačovat výhodnějším poměrem podstupovaného rizika a výnosu. Platí pravidlo, že čím vyšší riziko investor podstupuje, tím vyšší by mělo být zhodnocení jeho vkladu.`
			}, {
				condition: (state, handler) => handler.inputs.vlastniKapital.get() > 0 && handler.inputs.cistyZisk.get() > 0 && state.value > 0,
				large: true,
				prefix: `
E > 0  
Hospodářský výsledek > 0  
EVA Equity > 0
				`,
				content: `Jedná se o žádoucí stav Generátory hodnoty plní svoji funkci – generují hodnotu pro vlastníky. Zároveň je míra rizika odpovídají požadovanému zhodnocení investice. Takovýto stav je dlouhodobě žádoucí. Nyní již záleží jen na samotné výši EVA Equity, čím vyšší tím více roste hodnota pro vlastníky.`
			}]
			// tslint:enable: no-trailing-whitespace
		}, 0)
	});

	return model;

}

export type TModel_FPI_MSP_2020_05_Handler = ReturnType<typeof Model_FPI_MSP_2020_05_Handler>;
